import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { flattenRequiredProductFields } from 'common/components/product-add/make-product-active/utils';

import * as minimumRequirementServices from 'services/minimumRequirements';
import { PRIMARY_ERRORS_FOR_ACTIVE_PRODUCT } from 'static/productValidation';

const defaultEntityQueryOptions = {
  staleTime: Infinity,
  retry: 1,
};

export const getEntityQueryOptions = (entityType) => {
  const lowercaseEntityType = entityType ? entityType.toLowerCase() : 'product';

  const options = {
    product: {
      queryKey: ['product', 'get-all-minimum-requirement-product-field'],
      queryFn: async () => {
        return await minimumRequirementServices.getAllProductFields();
      },
      ...defaultEntityQueryOptions,
    },

    member: {
      queryKey: ['member', 'get-all-minimum-requirement-member-field'],
      queryFn: async () => {
        return await minimumRequirementServices.getAllMemberFields();
      },
      ...defaultEntityQueryOptions,
    },

    asset: {
      queryKey: ['dam', 'get-all-minimum-requirement-asset-field'],
      queryFn: async () => {
        return await minimumRequirementServices.getAllAssetFields();
      },
      ...defaultEntityQueryOptions,
    },
  };

  return options[lowercaseEntityType];
};

export const useAllMinimumRequirementProductFields = () => {
  const query = useQuery(getEntityQueryOptions('product'));

  return {
    allRequiredProductFields:
      useMemo(() => query?.data?.data?.schemas, [query?.data]) ?? [],

    flatRequiredProductFields: useMemo(
      () =>
        flattenRequiredProductFields({ allFields: query?.data?.data?.schemas }),
      [query?.data]
    ),
    ...query,
  };
};

const mapKeysToLowerCase = (obj) => {
  return Object.keys(obj).reduce((acc, key) => {
    acc[key.toLowerCase()] = obj[key];
    return acc;
  }, {});
};

export const useGetRequirementsForActiveProduct = () => {
  const { flatRequiredProductFields } = useAllMinimumRequirementProductFields();

  return {
    allActiveRequirements: useMemo(
      () =>
        mapKeysToLowerCase({
          ...flatRequiredProductFields,
          ...PRIMARY_ERRORS_FOR_ACTIVE_PRODUCT,
        }),
      [flatRequiredProductFields]
    ),
  };
};

export const useAllMinimumRequirementMemberFields = () => {
  const query = useQuery(getEntityQueryOptions('member'));

  return {
    allRequiredMemberFields:
      useMemo(() => query?.data?.data?.fields, [query?.data]) ?? [],
    ...query,
  };
};

export const useAllMinimumRequirementAssetFields = () => {
  const query = useQuery(getEntityQueryOptions('asset'));

  return {
    allRequiredAssetFields:
      useMemo(() => query?.data?.data?.fields, [query?.data]) ?? [],
    ...query,
  };
};
export const useGetEntityProperties = (props) => {
  const { entityType: entityTypeProp, fetchOnMount = true } = props;

  const entityType = entityTypeProp ? entityTypeProp.toLowerCase() : 'product';

  const queryOptions = useMemo(() => {
    const options = getEntityQueryOptions(entityType);

    return {
      enabled: Boolean(fetchOnMount),
      select: (data) => {
        const fields = data?.data?.schemas || data?.data?.fields;
        return fields;
      },
      staleTime: Infinity,
      ...options,
    };
  }, [entityType, fetchOnMount]);

  const query = useQuery(queryOptions);

  const getEntityProperties = () => {
    return query?.data;
  };

  return {
    getEntityProperties,
    entityProperties: useMemo(() => query?.data ?? null, [query?.data]),
    isLoading: query.isLoading,
    hasData: !!query?.data,
  };
};
